/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';

import { Component, type ComponentDimensionOptions, Components, PanelItem, type ReferenceMap, type Size, Vertex3, useSubscription } from '@rta/rta-blueprint-library';

import { APPLIANCE_FONT_SIZE } from '@r-core/constants';
import { type SkuItemDimensions } from '@r-core/types';
import { DesignerOptions, DisplayMode } from '@r-core';

export type UsePanelItem = {
	refMap: ReferenceMap<string>;
	// id: string;
	// island: Island,
	// panel: Panel,
	// panelItemGroup: PanelItemGroup,
	// panelItem: PanelItem,
	_countertop: {
		overlap: Size;
	};
	_dimensions: SkuItemDimensions | ComponentDimensionOptions;
	_item: {
		corners: ReturnType<PanelItem['getCorners']>;
		offset: Vertex3;
		origin: Vertex3;
		size: Size;
		showHint: boolean;
	};
	_cutout: {
		corners: ReturnType<PanelItem['getCorners']>;
		offset: Vertex3;
		origin: Vertex3;
		size: Size;
	};
	_label: {
		origin: Vertex3;
		fontSize: number;
		align: string;
		show: boolean;
	};
	image: {
		origin: Vertex3;
		size: Size;
		show: boolean;
	};
	// _sku: Component;
	_svg: {
		item: ({ x, y, opacity }) => void | null;
		cutout: ({ x, y, opacity }) => void | null;
	};
	// _referenceMap: ReferenceMap;
	// _sleeveCompatible: boolean;
	// _sleeveDepth: number;
	// _isStandAlone: boolean;
};

export type UsePanelItemOptions = {
	displayMode?: DisplayMode;
	options?: Partial<DesignerOptions>;
	isCountertop?: boolean;
};

export const usePanelItem = (panelItem: PanelItem, hookOptions: UsePanelItemOptions = {}): UsePanelItem => {
	const { displayMode = DisplayMode.Panel, options = {}, isCountertop = false } = hookOptions;

	const [sizeEvent] = useSubscription(panelItem, false, [
		['item_size', 'updated'],
		['item_cutout', 'updated'],
		['item_sku', 'updated'],
		['component_size', 'updated'],
		['component_cutout', 'updated'],
		['component_custom_label', 'updated'],
		['component_sku', 'updated'],
		['component_image_correction', 'updated'],
		['component_model_offset', 'updated'],
		['component_model_rotation', 'updated'],
		['panel_item_full_height', 'updated'],
		['panel_item_rotation', 'updated']
	]);

	return useMemo(() => {
		// const {
		// 	controller: { kitchen, options, navigation }
		// } = useContext(AppContext);

		const components = new Components(null);

		// console.log('panelItemHelper', panelItem.refMap);

		const { island } = panelItem.refMap

		// -----------------------
		// Item
		// -----------------------
		const item: Component = components.findId(panelItem.item.component.id) ?? components.findSku(panelItem.item.component.sku || 'BLANK');
		// const { offsets } = panelItem;

		const showItemHint: boolean = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY && !isCountertop;
		// const cutoutOverhang = !isCountertop || isDropIn ? 0 : island.countertop.overhangs[FRONT_INDEX] || 0;
		// const isPassThrough: boolean = item.sku === 'PASSTHROUGH';

		const itemSize = panelItem.size;
		const itemCorners = panelItem.getCorners(false, isCountertop);
		// const itemOffset = isCountertop ? itemCorners.top[0] : itemCorners.front[0];
		const itemOffset = new Vertex3({
			dataIn: {
				x: itemCorners.front[0].x,
				y: itemCorners.front[0].y,
				z: itemCorners.top[3].y
			}
		});
		// const itemOffset = offsets.item;

		// -----------------------
		// Panel Cutout
		// -----------------------
		const cutoutSize: Size = panelItem.cutout;
		const cutoutCorners = panelItem.getCorners(true);
		// const cutoutOffset = isCountertop ? itemCorners.top[0] : cutoutCorners.front[0];
		const cutoutOffset = new Vertex3({
			dataIn: {
				x: cutoutCorners.front[0].x,
				y: cutoutCorners.front[0].y,
				z: cutoutCorners.top[3].y
			}
		});
		// const cutoutOffset = offsets.cutout;

		// -----------------------
		// countertop
		// -----------------------

		// front & top - overall width represents total width with facepanel.  It's missing the appliance overlap on the counter.
		const countertopOverlap: Size = {
			height:
				isCountertop && panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP
					? panelItem.item.size.depth - panelItem.item.cutout.depth - panelItem.item.distanceFrontOfFace
					: 0,
			width: isCountertop && panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP ? panelItem.item.size.width - panelItem.item.cutout.width : 0,
			depth: 0
		};

		// -----------------------
		// label
		// -----------------------
		// Label positioning logic:
		// - SVG coordinate system is Y-down (positive Y goes down)
		// - Labels are positioned relative to the item's center point
		// - textAnchor 'start' = left-aligned in SVG
		//
		// Default (sinks, CDIC, most items): TOP-LEFT of cutout
		// - X: left edge of cutout (negative half-width) with small padding
		// - Y: top edge of cutout (negative half-height) with font size offset
		//
		// Pizza ovens (standalone): BOTTOM-LEFT of cutout
		// - X: left edge (same as default)
		// - Y: bottom edge of cutout (positive half-height)

		const effectiveCutoutWidth = cutoutSize.width > 0 ? cutoutSize.width : panelItem.item.size.width;

		const labelOrigin: Vertex3 = new Vertex3({
			dataIn: {
				x: showItemHint
					? panelItem.isStandAlone
							? panelItem.sleeve === null
								? -(panelItem.item.size.width / 2) + 0.5
								: -(panelItem.item.size.width / 2) + 0.5
							: -(effectiveCutoutWidth / 2) + 0.5
					: panelItem.isStandAlone
							? panelItem.sleeve === null
								? (panelItem.item.size.width / 2) - 0.5
								: (panelItem.item.size.width / 4) - 1
							: ((cutoutSize.width > 0 ? cutoutSize.width : panelItem.item.size.width) / 2) - 0.5,
				y: showItemHint
					? -(island.islandSize.height / 2) - panelItem.center.y - (APPLIANCE_FONT_SIZE * 0.15)
					: panelItem.isStandAlone
							? -island.islandSize.height - (panelItem.sleeve === null ? panelItem.item.size.height : (panelItem.item.size.height / 2) + 2)
							: (itemSize.height / 2) - (panelItem.boundTop || panelItem.fullHeight ? 0 : (itemSize.height - cutoutSize.height) / 2) - 0.5,
				z: panelItem.isStandAlone
					? displayMode === DisplayMode.Overhead || isCountertop
						? (island.islandSize.depth / -2) + (panelItem.sleeve === null ? panelItem.item.size.height / 4 : panelItem.item.size.height / 2) - 1
						: (island.islandSize.depth / -2) - (panelItem.item.size.height / 2) - 1.5
						: -(cutoutSize.depth - cutoutOffset.z) + APPLIANCE_FONT_SIZE
				//	: (panelItem.center.z - ((cutoutSize.depth > 0 ? cutoutSize.depth : panelItem.item.size.depth)) + 1.5)
			}
		});

		const labelFontSize = APPLIANCE_FONT_SIZE * (options.appliance?.fontSizeFactor ?? 1);
		const labelAlign = showItemHint ? 'start' : (panelItem.isStandAlone ? 'middle' : 'end');
		const showLabel = panelItem.item.showLabel && (
			(showItemHint && (displayMode === DisplayMode.Manufacturing || displayMode === DisplayMode.InlineManufacturing || displayMode === DisplayMode.Panel)) ||
			(options.appliance?.showLabel ?? true)
		);
		// -----------------------
		// image
		// -----------------------
		// Use the panelItem's own component for imageCorrection (reflects per-instance overrides),
		// falling back to the catalog entry for static properties like showImage, dimensions, svg.
		const { imageCorrection } = panelItem.item.component;
		const showImage = item?.showImage ?? false;
		const imageOrigin = new Vertex3({
			dataIn: {
				x: (itemSize.width / -2) + (imageCorrection?.x ?? 0),
				y: (itemSize.height / -2) + (imageCorrection?.y ?? 0),
				z: (itemSize.depth / -2) - (imageCorrection?.z ?? 0)
			}
		});

		// add the image correction to the size (some images have accessories that are not represented in the component size spec)
		const imageSize = {
			width: (imageCorrection?.width ?? 0) + itemSize.width,
			height: (imageCorrection?.height ?? 0) + itemSize.height,
			depth: (imageCorrection?.depth ?? 0) + itemSize.depth
		};

		// -----------------------
		// Dimensions
		// -----------------------

		// -----------------------
		const processedPanelItem: UsePanelItem = {
			refMap: panelItem.refMap,
			// id,
			// island,
			// panel,
			// panelItemGroup,
			// panelItem,
			_countertop: {
				overlap: countertopOverlap
			},
			_dimensions: item?.dimensions ?? {
			inside: {
				vertical: { offset: 0, ignore: false, render: true },
				horizontal: { offset: 0, ignore: false, render: true }
			},
			outside: {
				vertical: { ignore: false, render: true },
				horizontal: { ignore: false, render: true }
			}
		},
			image: {
				origin: imageOrigin,
				size: imageSize,
				show: showImage
			},
			_item: {
				origin: panelItem.center as Vertex3,
				corners: itemCorners,
				offset: itemOffset,
				size: itemSize,
				showHint: showItemHint
			},
			_label: {
				origin: labelOrigin,
				show: showLabel,
				fontSize: labelFontSize,
				align: labelAlign
			},
			_cutout: {
				origin: panelItem.center.clone().add(new Vertex3({
					dataIn: {
						x: 0,
						y: panelItem.boundTop ? (itemSize.height - cutoutSize.height) / 2 : panelItem.boundBottom ? (itemSize.height - cutoutSize.height) / 2 : 0,
						z: 0
					}
				})),
				corners: panelItem.getCorners(true),
				offset: cutoutOffset,
				size: cutoutSize
			},
			// _sku: item,
			_svg: {
				item: item?.svg?.item ?? null,
				cutout: item?.svg?.cutout ?? null
			}
			// _referenceMap: referenceMap,
			// _sleeveCompatible: item.sleeveCompatible,
			// _sleeveDepth: item.sleeveDepth,
			// _isStandAlone: panelItem.isStandAlone
		};

		// console.log(`usePanelItem - ${panelItem.item.sku} / ${isCountertop}`, processedPanelItem);

		return processedPanelItem;
	}, [panelItem, panelItem.center.x, panelItem.center.y, panelItem.center.z, displayMode, isCountertop, sizeEvent]);
};
